/**
 * URL serialization/deserialization utilities for Grading Portal filter state.
 *
 * Enables shareable URLs by persisting filter, pagination, and sort state
 * in URL search parameters.
 */

import {
    type SubmissionsFilters,
    type SortColumn,
    type SortDirection,
    type GradingStatus,
} from '../api/gradingPortalApi.types';

// ============================================================================
// Constants
// ============================================================================

export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 25;
export const DEFAULT_SORT: SortColumn = 'urgency_score';
export const DEFAULT_DIRECTION: SortDirection = 'asc';

/**
 * Default filter state for new sessions without URL params.
 * Shows submissions that need grading.
 */
export const DEFAULT_GRADING_STATUS: GradingStatus[] = [
    'submitted',
    'provisional',
    'awaiting_similarity_and_originality',
];

const VALID_SORT_COLUMNS: SortColumn[] = [
    'urgency_score',
    'grading_status',
    'assigned_status',
    'cohort_name',
    'requirement_identifier',
    'submitted_at',
    'due_date',
    'days_submitted_after_deadline',
    'days_since_submit',
    'days_until_graduation',
    'grading_deadline',
    'date_graded',
    'student_name',
    'student_email',
    'submission_attempt_number',
];

const VALID_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

const VALID_GRADING_STATUSES: GradingStatus[] = [
    'graded',
    'waived',
    'provisional',
    'awaiting_similarity_and_originality',
    'submitted',
    '-',
];

// Array filter keys that use comma-separated format in URL
const ARRAY_FILTER_KEYS = [
    'gradingStatus',
    'programType',
    'cohortId',
    'requirementIdentifier',
    'graderId',
    'projectVariant',
] as const;

// ============================================================================
// Serialization (State → URL)
// ============================================================================

/**
 * Serialize an array value to a comma-separated string.
 * Returns undefined if array is empty.
 */
function serializeArray(values: string[] | undefined): string | undefined {
    if (!values || values.length === 0) return undefined;
    return values.join(',');
}

/**
 * Serialize filter state and pagination to URL search params.
 *
 * Design decisions:
 * - Default pagination/sort values are omitted to keep URLs clean
 * - Empty filters are omitted
 * - Array values use comma-separated format
 */
// oxlint-disable-next-line max-lines-per-function
export function filtersToSearchParams(
    filters: SubmissionsFilters,
    page: number,
    perPage: number,
    sort: SortColumn,
    direction: SortDirection,
): URLSearchParams {
    const params = new URLSearchParams();

    // Pagination (only if non-default)
    if (page !== DEFAULT_PAGE) {
        params.set('page', String(page));
    }
    if (perPage !== DEFAULT_PER_PAGE) {
        params.set('perPage', String(perPage));
    }

    // Sort (only if non-default)
    if (sort !== DEFAULT_SORT) {
        params.set('sort', sort);
    }
    if (direction !== DEFAULT_DIRECTION) {
        params.set('direction', direction);
    }

    // Grading Status (array) - special handling to distinguish "not set" from "explicitly cleared"
    // - undefined: Don't set param (fresh load will apply defaults)
    // - []: Set empty param (explicitly cleared, show all)
    // - ['submitted', ...]: Set comma-separated values
    if (filters.gradingStatus !== undefined) {
        const gradingStatusArray = Array.isArray(filters.gradingStatus)
            ? filters.gradingStatus
            : [filters.gradingStatus];
        params.set('gradingStatus', gradingStatusArray.join(','));
    }

    // Program Type (array)
    const programTypeArray = normalizeToArray(filters.programType);
    const programTypeStr = serializeArray(programTypeArray);
    if (programTypeStr !== undefined) {
        params.set('programType', programTypeStr);
    }

    // Cohort ID (array)
    const cohortIdArray = normalizeToArray(filters.cohortId);
    const cohortIdStr = serializeArray(cohortIdArray);
    if (cohortIdStr !== undefined) {
        params.set('cohortId', cohortIdStr);
    }

    // Requirement Identifier (array)
    const requirementIdentifierArray = normalizeToArray(filters.requirementIdentifier);
    const requirementIdentifierStr = serializeArray(requirementIdentifierArray);
    if (requirementIdentifierStr !== undefined) {
        params.set('requirementIdentifier', requirementIdentifierStr);
    }

    // Grader ID (array)
    const graderIdArray = normalizeToArray(filters.graderId);
    const graderIdStr = serializeArray(graderIdArray);
    if (graderIdStr !== undefined) {
        params.set('graderId', graderIdStr);
    }

    // Project Variant (array)
    const projectVariantArray = normalizeToArray(filters.projectVariant);
    const projectVariantStr = serializeArray(projectVariantArray);
    if (projectVariantStr !== undefined) {
        params.set('projectVariant', projectVariantStr);
    }

    // Student Email (string)
    if (filters.studentEmail) {
        params.set('studentEmail', filters.studentEmail);
    }

    // Older Than Days (number)
    if (filters.olderThanDays !== undefined) {
        params.set('olderThanDays', String(filters.olderThanDays));
    }

    return params;
}

// ============================================================================
// Deserialization (URL → State)
// ============================================================================

/**
 * Convert a value to an array if it isn't already.
 */
function normalizeToArray<T>(value: T | T[] | undefined): T[] {
    if (value === undefined) return [];
    return Array.isArray(value) ? value : [value];
}

/**
 * Parse a comma-separated string into an array.
 * Returns empty array if string is empty or undefined.
 */
function parseArray(value: string | null): string[] {
    if (!value) return [];
    return value.split(',').filter(v => v.length > 0);
}

/**
 * Parse and validate grading status values.
 * Invalid values are filtered out.
 */
function parseGradingStatuses(value: string | null): GradingStatus[] {
    const values = parseArray(value);
    return values.filter((v): v is GradingStatus => VALID_GRADING_STATUSES.includes(v as GradingStatus));
}

/**
 * Parse a positive integer from string.
 * Returns undefined if invalid.
 */
function parsePositiveInt(value: string | null): number | undefined {
    if (!value) return undefined;
    const num = parseInt(value, 10);
    if (Number.isNaN(num) || num < 1) return undefined;
    return num;
}

/**
 * Parse sort column from string.
 * Returns default if invalid.
 */
function parseSortColumn(value: string | null): SortColumn {
    if (!value) return DEFAULT_SORT;
    if (VALID_SORT_COLUMNS.includes(value as SortColumn)) {
        return value as SortColumn;
    }
    return DEFAULT_SORT;
}

/**
 * Parse sort direction from string.
 * Returns default if invalid.
 */
function parseSortDirection(value: string | null): SortDirection {
    if (!value) return DEFAULT_DIRECTION;
    if (VALID_DIRECTIONS.includes(value as SortDirection)) {
        return value as SortDirection;
    }
    return DEFAULT_DIRECTION;
}

interface ParsedUrlState {
    filters: SubmissionsFilters;
    page: number;
    perPage: number;
    sort: SortColumn;
    direction: SortDirection;
    /** ID of submission to open in evaluation modal on page load */
    openSubmissionId: string | null;
}

/**
 * Deserialize URL search params to filter state and pagination.
 *
 * Design decisions:
 * - Invalid values fall back to defaults
 * - Unknown params are ignored
 * - If no gradingStatus in URL and URL has other params, use empty (explicit clear)
 * - If URL is completely empty, use default grading status
 */
// oxlint-disable-next-line max-lines-per-function
export function searchParamsToFilters(searchParams: URLSearchParams): ParsedUrlState {
    // Check if URL has any recognized params (to distinguish "fresh load" from "explicitly cleared")
    const hasAnyParams = Array.from(searchParams.keys()).some(key =>
        ['page', 'perPage', 'sort', 'direction', ...ARRAY_FILTER_KEYS, 'studentEmail', 'olderThanDays'].includes(key),
    );

    // Pagination
    const page = parsePositiveInt(searchParams.get('page')) ?? DEFAULT_PAGE;
    const perPage = parsePositiveInt(searchParams.get('perPage')) ?? DEFAULT_PER_PAGE;

    // Sorting
    const sort = parseSortColumn(searchParams.get('sort'));
    const direction = parseSortDirection(searchParams.get('direction'));

    // Filters
    const filters: SubmissionsFilters = {};

    // Grading Status - special handling to distinguish "not set" from "explicitly cleared"
    // - param absent + no other params: Fresh load, apply defaults
    // - param absent + has other params: Leave undefined (implicit)
    // - param = "": Explicitly cleared, set empty array (show all)
    // - param = "submitted,...": Use those values
    const gradingStatusParam = searchParams.get('gradingStatus');
    const hasGradingStatusParam = searchParams.has('gradingStatus');

    if (hasGradingStatusParam) {
        if (gradingStatusParam === '') {
            // Explicitly cleared - set empty array to signal "show all"
            filters.gradingStatus = [];
        } else {
            const gradingStatuses = parseGradingStatuses(gradingStatusParam);
            if (gradingStatuses.length > 0) {
                filters.gradingStatus = gradingStatuses;
            }
            // If all values were invalid, leave undefined
        }
    } else if (!hasAnyParams) {
        // No URL params at all - apply default filter for fresh page load
        filters.gradingStatus = DEFAULT_GRADING_STATUS;
    }
    // If hasAnyParams but no gradingStatus param, leave it undefined

    // Program Type
    const programTypes = parseArray(searchParams.get('programType'));
    if (programTypes.length > 0) {
        filters.programType = programTypes;
    }

    // Cohort ID
    const cohortIds = parseArray(searchParams.get('cohortId'));
    if (cohortIds.length > 0) {
        filters.cohortId = cohortIds;
    }

    // Requirement Identifier
    const requirementIdentifiers = parseArray(searchParams.get('requirementIdentifier'));
    if (requirementIdentifiers.length > 0) {
        filters.requirementIdentifier = requirementIdentifiers;
    }

    // Grader ID
    const graderIds = parseArray(searchParams.get('graderId'));
    if (graderIds.length > 0) {
        filters.graderId = graderIds;
    }

    // Project Variant
    const projectVariants = parseArray(searchParams.get('projectVariant'));
    if (projectVariants.length > 0) {
        filters.projectVariant = projectVariants;
    }

    // Student Email
    const studentEmail = searchParams.get('studentEmail');
    if (studentEmail) {
        filters.studentEmail = studentEmail;
    }

    // Older Than Days
    const olderThanDays = parsePositiveInt(searchParams.get('olderThanDays'));
    if (olderThanDays !== undefined) {
        filters.olderThanDays = olderThanDays;
    }

    // Open Submission ID (for deep-linking to evaluation modal)
    const openSubmissionId = searchParams.get('openSubmissionId');

    return { filters, page, perPage, sort, direction, openSubmissionId };
}

/**
 * Check if two filter objects are equal.
 * Handles array normalization for comparison.
 */
export function areFiltersEqual(a: SubmissionsFilters, b: SubmissionsFilters): boolean {
    // Compare array filters
    const arrayFiltersEqual = ARRAY_FILTER_KEYS.every(key => {
        const aArray = normalizeToArray(a[key] as string | string[] | undefined);
        const bArray = normalizeToArray(b[key] as string | string[] | undefined);
        if (aArray.length !== bArray.length) return false;
        const aSorted = [...aArray].sort();
        const bSorted = [...bArray].sort();
        return aSorted.every((val, i) => val === bSorted[i]);
    });

    if (!arrayFiltersEqual) return false;

    // Compare scalar filters
    if (a.studentEmail !== b.studentEmail) return false;
    if (a.olderThanDays !== b.olderThanDays) return false;

    return true;
}
